import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import budgetEmpty from '../../assets/img/icons/budget-empty.svg';
import hourEmpty from '../../assets/img/icons/hour-empty.svg';
import LinearActivityBar from '../../components/ProgressBar/LinearActivityBar';
import {
  CardTextData,
  CicrularBarSection,
  ViewLessCard,
  ViewLessCardContainer,
} from '../../styledComponents/ProjectsNew';
import { CommonFlex, CommonText } from '../../styledComponents/common';
import { currencySymbolMap } from '../../utils/currencies';
import {
  BudgetLeftSection,
  PercentageBarSection,
  PercentageText,
  ProjectCardLabel,
  ProjectCardNoDataContainer,
  ProjectCardNoDataImg,
  ProjectCardNoDataText,
  ProjectCardText,
} from '../Projects/projectsStyles';
import HelperIcon from '../../components/HelperIcon/HelperIcon';

export const ProjectCardNoData = ({ type, text }) => {
  return (
    <ProjectCardNoDataContainer>
      <ProjectCardNoDataImg
        src={type === 'hour' ? hourEmpty : budgetEmpty}
        alt=""
      />
      {text && <ProjectCardNoDataText>{text}</ProjectCardNoDataText>}
    </ProjectCardNoDataContainer>
  );
};

const ViewLess = ({ work_order, selectedOrganization }) => {
  const { t } = useTranslation();
  const [budgetType, setBudgetType] = useState(null);
  const [budgetAmount, setBudgetAmount] = useState(null);
  const [spentHours, setSpentHours] = useState(null);
  const [budgetCost, setBudgetCost] = useState(null);
  const [budgetLeft, setBudgetLeft] = useState(null);
  const [hoursPercentage, setHoursPercentage] = useState(0);
  const [costPercentage, setCostPercentage] = useState(0);
  const [budgetLeftPercentage, setBudgetLeftPercentage] = useState(0);
  const [hasBillable, setHasBillable] = useState(false);
  const [billableAmount, setBillableAmount] = useState(0);
  const [uninvoicedAmount, setUninvoicedAmount] = useState(0);
  const [invoicedAmount, setInvoicedAmount] = useState(0);

  const [currency, setCurrency] = useState('');
  const orgtype = selectedOrganization ? selectedOrganization.type : 'operation';
  const isOperation = orgtype === 'operation';

  useEffect(() => {
    if (
      selectedOrganization &&
      selectedOrganization.configuration &&
      selectedOrganization.configuration.currency
    ) {
      setCurrency(selectedOrganization.configuration.currency);
    }
  }, [selectedOrganization]);

  useEffect(() => {
    if (work_order) {
      if (work_order.billable_id !== null) {
        setHasBillable(true);
        setBillableAmount(work_order.billable_amount.toFixed(2));
        setUninvoicedAmount(work_order.uninvoiced_amount.toFixed(2));
        setInvoicedAmount(
          (work_order.billable_amount - work_order.uninvoiced_amount).toFixed(
            2,
          ),
        );
      } else {
        setHasBillable(false);
      }
      if (work_order.spent_hours) {
        setSpentHours(getHours(work_order.spent_hours));
      } else {
        setSpentHours(null);
      }
      if (!work_order.budget) {
        setBudgetType(null);
        setBudgetAmount(null);
        setBudgetCost(null);
        setBudgetLeft(null);
        return;
      }
      if (work_order.budget) {
        setBudgetType(work_order.budget.budget_type);
        if (work_order.budget.budget_type === 'work_order_fees') {
          if (work_order.budget.amount) {
            setBudgetAmount((work_order.budget.amount / 100).toFixed(2));
            if (work_order.budget.budget_left !== null) {
              let leftBudget = (
                (work_order.budget.budget_left / work_order.budget.amount) *
                100
              ).toFixed(1);
              setBudgetLeft((work_order.budget.budget_left / 100).toFixed(2));
              setBudgetLeftPercentage(leftBudget);
            }
            if (work_order.budget.cost !== null) {
              let cost = (
                (work_order.budget.cost / work_order.budget.amount) *
                100
              ).toFixed(1);
              setBudgetCost(work_order.budget.cost);
              setCostPercentage(
                work_order.budget.cost > work_order.budget.amount ? 100 : cost,
              );
            } else if (!work_order.budget.cost) {
              setCostPercentage(0);
            }
          }
        } else if (work_order.budget.budget_type === 'work_order_hours') {
          if (work_order.budget.amount) {
            setBudgetAmount(work_order.budget.amount);
            if (work_order.budget.budget_left !== null) {
              let leftBudget = (
                (work_order.budget.budget_left / work_order.budget.amount) *
                100
              ).toFixed(1);
              setBudgetLeft(work_order.budget.budget_left);
              setBudgetLeftPercentage(leftBudget);
            }
            if (work_order.budget.spent_hours) {
              let hourPercentage = (
                (getTimeInHours(work_order.budget.spent_hours) /
                  work_order.budget.amount) *
                100
              ).toFixed(1);
              setHoursPercentage(
                parseInt(work_order.budget.spent_hours.split(':')[0]) >
                  work_order.budget.amount
                  ? 100
                  : hourPercentage,
              );
            }
          }
        }
      }
    }
  }, [work_order]);

  const getHours = time => {
    let hours = time.split(':');
    return `${hours[0]}h ${hours[1]}m`;
  };

  const getTimeInHours = time => {
    let splitTime = time.split(':');

    return parseInt(splitTime[0]);
  };

  return (
    <ViewLessCardContainer columns={isOperation ? "repeat(4, 1fr)" : "repeat(5, 1fr)"}>
      <ViewLessCard>
        <div>
          <CommonText fontSize="12px">
            {budgetType && budgetType === 'work_order_hours'
              ? 'Budgeted Hours'
              : 'Total Budget'}
          </CommonText>
          {budgetAmount ? (
            <CardTextData margin="4px 0 0" fontSize="14px">
              {budgetType && budgetType === 'project_fees'
                ? `${currencySymbolMap[currency]} `
                : ``}
              {budgetAmount}
              {budgetType && budgetType === 'work_order_hours' ? `hours` : ``}
            </CardTextData>
          ) : (
            <ProjectCardNoData type="budget" text="Budget not set" />
          )}
        </div>
        {budgetLeft !== null && (
          <BudgetLeftSection>
            <ProjectCardLabel>
              {budgetType && budgetType === 'work_order_fees'
                ? 'Left'
                : 'Remaining'}
            </ProjectCardLabel>
            <ProjectCardText>
              {budgetType === 'work_order_fees'
                ? `${currencySymbolMap[currency]} ${budgetLeft}`
                : `${budgetLeft} hours`}
            </ProjectCardText>
          </BudgetLeftSection>
        )}
      </ViewLessCard>
      <ViewLessCard>
        <div>
          <CommonFlex> 
            <CommonText fontSize="12px">
            Spent{' '}
            {budgetType && budgetType === 'work_order_hours'
              ? 'Hours'
              : 'Amount'}
          </CommonText>
          <HelperIcon  text={t("amount_calculated_based_on_worked_hours_and_method_of_billing")} />
          </CommonFlex>
          {budgetAmount ? (
            <CardTextData margin="4px 0 0" fontSize="14px">
              {budgetType && budgetType === 'work_order_hours'
                ? spentHours
                  ? spentHours
                  : '0h 00m'
                : budgetCost
                ? `${currencySymbolMap[currency]} ${(budgetCost / 100).toFixed(
                    2,
                  )}`
                : budgetAmount
                ? `${currencySymbolMap[currency]} 0.00`
                : 'N/A'}
            </CardTextData>
          ) : (
            <ProjectCardNoData type="budget" text="Budget not set" />
          )}
        </div>
        {budgetAmount ? (
          <PercentageBarSection>
            <LinearActivityBar
              width="100%"
              height="8px"
              borderRadius="4px"
              yellow="#20bead"
              red="#20bead"
              green="#20bead"
              percent={
                budgetType === 'work_order_hours' && hoursPercentage
                  ? hoursPercentage
                  : budgetType === 'work_order_fees' && costPercentage
                  ? costPercentage
                  : 0
              }
              marginTop="0"
              backgroundColor="#B5B5B5"
            />
            <PercentageText>
              {budgetType === 'work_order_fees' && costPercentage
                ? costPercentage
                : budgetType === 'work_order_hours' && hoursPercentage
                ? hoursPercentage
                : 0}
              %
            </PercentageText>
          </PercentageBarSection>
        ) : null}
      </ViewLessCard>
      <ViewLessCard>
        <div>
          <CommonFlex gap="6px" alignItems="center">
            <CommonText fontSize="12px">
              Spent{' '}
              {budgetType && budgetType === 'work_order_hours'
                ? 'Amount'
                : 'Hours'}
            </CommonText>
            {budgetType && budgetType !== 'work_order_hours' && (
              <HelperIcon text={t("hours_spent_on_the_project")} />
            )}
          </CommonFlex>
          {budgetType && budgetType === 'work_order_hours' ? (
            <ProjectCardNoData type="budget" />
          ) : spentHours && spentHours !== '00h 00m' ? (
            <CardTextData margin="4px 0 0" fontSize="14px">
              {spentHours}
            </CardTextData>
          ) : (
            <ProjectCardNoData type="hour" text="Time not tracked yet" />
          )}
        </div>
      </ViewLessCard>
      <ViewLessCard>
        <div>
          <CommonFlex gap="6px" alignItems="center">
            <CommonText fontSize="12px">Billable Amount</CommonText>
            <HelperIcon text={t("amount_calculated_for_billing")} />
          </CommonFlex>
          {hasBillable ? (
            <CardTextData margin="4px 0 0" fontSize="14px">
              {currencySymbolMap[currency]} {billableAmount}
            </CardTextData>
          ) : (
            <ProjectCardNoData type="budget" text="Billing not set" />
          )}
        </div>
      </ViewLessCard>
      {!isOperation && (
        <ViewLessCard>
          <div>
          <CommonFlex gap="6px" alignItems="center">
              <CommonText fontSize="12px">Uninvoiced Amount</CommonText>
            <HelperIcon text={t("amount_yet_to_be_invoiced")} />
          </CommonFlex>
            {hasBillable ? (
              <CardTextData
                margin="4px 0 0"
              fontSize="14px"
              textColor={
                uninvoicedAmount && uninvoicedAmount < 0 ? '#FE5969' : '#20bead'
              }
            >
              {currencySymbolMap[currency]} {uninvoicedAmount}
            </CardTextData>
          ) : (
            <ProjectCardNoData type="budget" text="Billing not set" />
          )}
        </div>
        {hasBillable && invoicedAmount && (
          <BudgetLeftSection>
            <ProjectCardLabel>Invoiced</ProjectCardLabel>
            <ProjectCardText>
              {currencySymbolMap[currency]} {invoicedAmount}
            </ProjectCardText>
          </BudgetLeftSection>
        )}
      </ViewLessCard>)}
    </ViewLessCardContainer>
  );
};

export default ViewLess;
